import { parseZonedDateTime } from '@internationalized/date';
import { Edge } from '@xyflow/react';

import type { AutomationNode } from '../_components/editor/nodes/types';
import { AutomationRecord, AutomationStatus, TriggerType } from '../types';

import { automationFullFragment, webhookEndpointFragment } from './fragments';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';

/**
 * Converts a GraphQL Node union type to the frontend AutomationNode format.
 * The GraphQL schema uses typed nodes (BusinessEventTriggerNode, EmailActionNode, etc.)
 * while the frontend uses a generic Node with data.config structure.
 */
function convertGraphQLNodeToAutomationNode(
  gqlNode: Record<string, unknown>,
): AutomationNode {
  const {
    id,
    type,
    position,
    label,
    description,
    // Extract all other fields as config
    ...configFields
  } = gqlNode;

  let config: Record<string, unknown> = configFields;

  if (type === 'BusinessEventTrigger' && configFields.eventType) {
    const eventTypeStr = configFields.eventType as string;
    const dotIndex = eventTypeStr.indexOf('.');
    const entityType =
      dotIndex > -1 ? eventTypeStr.substring(0, dotIndex) : eventTypeStr;
    const eventCase =
      dotIndex > -1 ? eventTypeStr.substring(dotIndex + 1) : undefined;

    config = {
      source: {
        case: entityType,
        value: eventCase
          ? { event: { case: eventCase, value: {} } }
          : { event: { case: undefined } },
      },
      filterExpression:
        (configFields.filter as Record<string, unknown>)?.expression ?? '',
    };
  } else if (type === 'Loop') {
    config = {
      arrayExpression: configFields.loopArrayExpression ?? '',
      itemVariableName: configFields.loopItemVariableName ?? 'item',
      maxIterations: configFields.loopMaxIterations ?? 100,
    };
  }

  return {
    id: id as string,
    type: type as string,
    position: position as { x: number; y: number },
    data: {
      label: label as string,
      description: description as string | undefined,
      config,
    },
  };
}

/**
 * Converts a GraphQL Edge to React Flow Edge format.
 */
function convertGraphQLEdgeToReactFlowEdge(
  gqlEdge: Record<string, unknown>,
): Edge {
  return {
    id: gqlEdge.id as string,
    source: gqlEdge.source as string,
    target: gqlEdge.target as string,
    sourceHandle: gqlEdge.sourceHandle as string | undefined,
  };
}

export const automationDetailQueryAtom = createQueryAtomFactory(
  'automations.detail',
  (params: { id: string }) => ({
    automation: {
      __args: {
        id: params.id,
      },
      ...automationFullFragment,
      webhookEndpoint: webhookEndpointFragment,
    },
  }),
  {
    keepPreviousData: false,
    select: ({ automation }): AutomationRecord | null => {
      if (!automation) return null;

      return {
        id: automation.id,
        name: automation.name,
        description: automation.description ?? null,
        status: automation.status as AutomationStatus,
        triggerType: automation.triggerType as TriggerType,
        triggerConfig: automation.triggerConfig ?? {},
        nodes: Array.isArray(automation.nodes)
          ? automation.nodes.map(convertGraphQLNodeToAutomationNode)
          : [],
        edges: Array.isArray(automation.edges)
          ? automation.edges.map(convertGraphQLEdgeToReactFlowEdge)
          : [],
        workflowId: automation.workflowId ?? null,
        workflowRunId: automation.workflowRunId ?? null,
        organizationId: automation.organizationId ?? '',
        webhookEndpoint: automation.webhookEndpoint
          ? {
              id: automation.webhookEndpoint.id,
              automationId: automation.webhookEndpoint.automationId,
              path: automation.webhookEndpoint.path,
              secret: automation.webhookEndpoint.secret,
              isActive: automation.webhookEndpoint.isActive,
              lastUsedAt: automation.webhookEndpoint.lastUsedAt
                ? parseZonedDateTime(automation.webhookEndpoint.lastUsedAt)
                : null,
              timestamps: {
                createdAt: parseZonedDateTime(
                  automation.webhookEndpoint.timestamps.createdAt,
                ),
                updatedAt: parseZonedDateTime(
                  automation.webhookEndpoint.timestamps.updatedAt,
                ),
              },
            }
          : null,
        timestamps: {
          createdAt: parseZonedDateTime(automation.timestamps.createdAt),
          updatedAt: parseZonedDateTime(automation.timestamps.updatedAt),
        },
      };
    },
  },
);
